import React, { useState } from 'react';
import { Plus, X, Upload, Trash2, Database, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from 'equal-ds-ui';
import type { SandboxDataset, SandboxFile } from '../../../types/analytics-sandbox';

const DS_COLORS = [
  { color: '#4f6ef7', bg: '#eef2ff' },
  { color: '#7c3aed', bg: '#f5f3ff' },
  { color: '#059669', bg: '#ecfdf5' },
];

interface SandboxDatasetsDrawerProps {
  open: boolean;
  datasets: SandboxDataset[];
  onClose: () => void;
  onNewDataset: () => void;
  onUploadFiles: (datasetId: string) => void;
  onDeleteDataset: (dataset: SandboxDataset) => void;
  onDeleteFile: (datasetId: string, file: SandboxFile) => void;
  onLoadFiles?: (datasetId: string) => void;
  onAddConsents: (datasetId: string, consentIds: string[]) => Promise<void>;
  onRemoveConsent: (datasetId: string, consentId: string) => Promise<void>;
  maxPdfItems?: number;
  maxConsentItems?: number;
}

export const SandboxDatasetsDrawer: React.FC<SandboxDatasetsDrawerProps> = ({
  open, datasets, onClose, onNewDataset, onUploadFiles, onDeleteDataset, onDeleteFile, onLoadFiles,
  onAddConsents, onRemoveConsent, maxPdfItems = 10, maxConsentItems = 10,
}) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [consentInputOpen, setConsentInputOpen] = useState<Set<string>>(new Set());
  const [consentInput, setConsentInput] = useState<Record<string, string>>({});
  const [consentError, setConsentError] = useState<Record<string, string | null>>({});
  const [consentSaving, setConsentSaving] = useState<Record<string, boolean>>({});

  const toggle = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        onLoadFiles?.(id);
      }
      return next;
    });
  };

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          display: open ? 'block' : 'none',
          position: 'fixed', top: 64, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,.25)', zIndex: 1200,
        }}
      />
      {/* Drawer */}
      <div style={{
        position: 'fixed', top: 64, right: 0, bottom: 0, width: 520,
        maxWidth: '94vw', background: '#fff',
        boxShadow: '-4px 0 30px rgba(0,0,0,.12)', zIndex: 1201,
        display: 'flex', flexDirection: 'column',
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform .25s cubic-bezier(.4,0,.2,1)',
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Datasets</div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 1 }}>PDF &amp; consent ID collections used across runs</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Button variant="primary" size="sm" onClick={onNewDataset}>
              <Plus style={{ width: 12, height: 12 }} />
              New Dataset
            </Button>
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 5, fontSize: 16, color: '#6b7280', lineHeight: 1, display: 'inline-flex', alignItems: 'center' }}>✕</button>
          </div>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', background: '#f8fafc' }}>
          {datasets.length === 0 ? (
            <div style={{ padding: '48px 0', textAlign: 'center', color: '#9ca3af' }}>
              <Database style={{ width: 36, height: 36, margin: '0 auto 10px', strokeWidth: 1.5 }} />
              <div style={{ fontSize: 13 }}>No datasets yet. Create one to get started.</div>
            </div>
          ) : datasets.map((ds, idx) => {
            const isOpen = expanded.has(ds.id);
            const c = DS_COLORS[idx % DS_COLORS.length];
            return (
              <div key={ds.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, marginBottom: 10, overflow: 'hidden' }}>
                {/* Accordion header */}
                <div
                  onClick={() => toggle(ds.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px', cursor: 'pointer', userSelect: 'none' }}
                >
                  <div style={{ width: 36, height: 36, borderRadius: 9, background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Database style={{ width: 16, height: 16, color: c.color }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14.5, fontWeight: 500, color: '#111827' }}>{ds.name}</div>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 1 }}>{ds.fileCount ?? ds.files.length} file{(ds.fileCount ?? ds.files.length) !== 1 ? 's' : ''} · {ds.consents.length} consent{ds.consents.length !== 1 ? 's' : ''} · {ds.runCount} run{ds.runCount !== 1 ? 's' : ''}</div>
                  </div>
                  <button
                    onClick={e => { e.stopPropagation(); onDeleteDataset(ds); }}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 5, borderRadius: 5, display: 'inline-flex', color: '#9ca3af' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#fef2f2'; (e.currentTarget as HTMLButtonElement).style.color = '#dc2626'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = '#9ca3af'; }}
                  >
                    <Trash2 style={{ width: 14, height: 14 }} />
                  </button>
                  {isOpen ? <ChevronDown style={{ width: 14, height: 14, color: '#9ca3af' }} /> : <ChevronRight style={{ width: 14, height: 14, color: '#9ca3af' }} />}
                </div>

                {/* Expanded: files + consents */}
                {isOpen && (
                  <div style={{ borderTop: '1px solid #f1f5f9', padding: '16px 20px 18px' }}>

                    {/* ── PDF Files section ── */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', padding: '2px 7px', borderRadius: 5, background: '#f1f5f9', color: '#475569', textTransform: 'uppercase' }}>PDF Files</span>
                        <span style={{ fontSize: 12, color: '#9ca3af' }}>{ds.fileCount ?? ds.files.length} file{(ds.fileCount ?? ds.files.length) !== 1 ? 's' : ''} · max {maxPdfItems} per run</span>
                      </div>
                      <Button variant="primary" size="sm" onClick={() => onUploadFiles(ds.id)}>
                        <Upload style={{ width: 12, height: 12 }} />
                        Upload PDFs
                      </Button>
                    </div>
                    {ds.files.length === 0 ? (
                      <div style={{ padding: '10px 0 14px', textAlign: 'center', fontSize: 12.5, color: '#9ca3af' }}>No PDFs yet — upload to get started.</div>
                    ) : (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', padding: '4px 4px 6px', borderBottom: '1px solid #f1f5f9' }}>
                          <span style={{ fontSize: 10.5, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.06em' }}>Filename</span>
                        </div>
                        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                          {ds.files.map((f, fi) => (
                            <div key={fi} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 4px', borderBottom: fi < ds.files.length - 1 ? '1px solid #f9fafb' : 'none' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth={2} style={{ width: 13, height: 13, flexShrink: 0 }}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                <span style={{ fontSize: 12.5, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                              </div>
                              <button
                                onClick={() => onDeleteFile(ds.id, f)}
                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 5, borderRadius: 5, display: 'inline-flex', color: '#9ca3af', flexShrink: 0 }}
                                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#fef2f2'; (e.currentTarget as HTMLButtonElement).style.color = '#dc2626'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = '#9ca3af'; }}
                              >
                                <X style={{ width: 11, height: 11 }} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* ── Consent IDs section ── */}
                    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 14 }}>
                      {/* Section header */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', padding: '2px 7px', borderRadius: 5, background: '#fef3c7', color: '#b45309', textTransform: 'uppercase' }}>Consent IDs</span>
                          <span style={{ fontSize: 12, color: '#9ca3af' }}>{ds.consents.length} consent ID{ds.consents.length !== 1 ? 's' : ''} · max {maxConsentItems} per run</span>
                        </div>
                        <button
                          className="sb-consent-add-btn"
                          onClick={() => setConsentInputOpen(prev => {
                            const next = new Set(prev);
                            if (next.has(ds.id)) next.delete(ds.id); else next.add(ds.id);
                            return next;
                          })}
                        >
                          <Plus style={{ width: 11, height: 11 }} />
                          Add Consent IDs
                        </button>
                      </div>

                      {/* Expandable add panel */}
                      {consentInputOpen.has(ds.id) && (
                        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 12, overflow: 'hidden' }}>
                          {/* Panel header */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>Add Consent IDs</span>
                            <button
                              onClick={() => {
                                setConsentInput(prev => ({ ...prev, [ds.id]: '' }));
                                setConsentError(prev => ({ ...prev, [ds.id]: null }));
                                setConsentInputOpen(prev => { const next = new Set(prev); next.delete(ds.id); return next; });
                              }}
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 4, color: '#9ca3af', display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}
                              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = '#374151'; (e.currentTarget as HTMLButtonElement).style.background = '#e5e7eb'; }}
                              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = '#9ca3af'; (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                            >
                              <X style={{ width: 14, height: 14 }} />
                            </button>
                          </div>
                          {/* Panel body */}
                          <div style={{ padding: '12px 14px' }}>
                            <textarea
                              rows={4}
                              placeholder="One consent ID per line, or separate by comma…"
                              value={consentInput[ds.id] || ''}
                              onChange={e => setConsentInput(prev => ({ ...prev, [ds.id]: e.target.value }))}
                              style={{ resize: 'vertical', fontFamily: 'monospace' }}
                            />
                            {consentError[ds.id] && (
                              <div style={{ fontSize: 11.5, color: '#dc2626', marginTop: 4 }}>{consentError[ds.id]}</div>
                            )}
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => {
                                  setConsentInput(prev => ({ ...prev, [ds.id]: '' }));
                                  setConsentError(prev => ({ ...prev, [ds.id]: null }));
                                  setConsentInputOpen(prev => { const next = new Set(prev); next.delete(ds.id); return next; });
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                variant="primary"
                                size="sm"
                                disabled={!consentInput[ds.id]?.trim() || !!consentSaving[ds.id]}
                                onClick={async () => {
                                  const raw = consentInput[ds.id] || '';
                                  const ids = raw.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
                                  if (ids.length === 0) return;
                                  setConsentSaving(prev => ({ ...prev, [ds.id]: true }));
                                  setConsentError(prev => ({ ...prev, [ds.id]: null }));
                                  try {
                                    await onAddConsents(ds.id, ids);
                                    setConsentInput(prev => ({ ...prev, [ds.id]: '' }));
                                    setConsentInputOpen(prev => { const next = new Set(prev); next.delete(ds.id); return next; });
                                  } catch (err: any) {
                                    setConsentError(prev => ({ ...prev, [ds.id]: err?.response?.data?.message || err?.message || 'Failed to add consent IDs' }));
                                  } finally {
                                    setConsentSaving(prev => ({ ...prev, [ds.id]: false }));
                                  }
                                }}
                              >
                                {consentSaving[ds.id] ? 'Adding…' : 'Add IDs'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Consent IDs list */}
                      {ds.consents.length === 0 && !consentInputOpen.has(ds.id) && (
                        <div style={{ padding: '8px 0', fontSize: 12.5, color: '#9ca3af', textAlign: 'center' }}>No consent IDs yet.</div>
                      )}
                      {ds.consents.length > 0 && (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', padding: '4px 4px 6px', borderBottom: '1px solid #f1f5f9' }}>
                            <span style={{ fontSize: 10.5, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.06em' }}>Consent ID</span>
                          </div>
                          <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                            {ds.consents.map((cid, ci) => (
                              <div key={ci} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 4px', borderBottom: ci < ds.consents.length - 1 ? '1px solid #f9fafb' : 'none' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                                  <svg viewBox="0 0 24 24" fill="none" style={{ width: 13, height: 13, flexShrink: 0 }}>
                                    <circle cx="12" cy="12" r="9" stroke="#d97706" strokeWidth={2} />
                                    <circle cx="12" cy="12" r="3" fill="#d97706" />
                                  </svg>
                                  <span style={{ fontSize: 12, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{cid}</span>
                                </div>
                                <button
                                  onClick={async () => {
                                    try { await onRemoveConsent(ds.id, cid); } catch { /* silent */ }
                                  }}
                                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 5, borderRadius: 5, display: 'inline-flex', color: '#9ca3af', flexShrink: 0 }}
                                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#fef2f2'; (e.currentTarget as HTMLButtonElement).style.color = '#dc2626'; }}
                                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = '#9ca3af'; }}
                                >
                                  <X style={{ width: 11, height: 11 }} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};
